<script setup>

import InlineMessage from "primevue/inlinemessage";
import RadioButton from "primevue/radiobutton";
import {computed, ref, watch} from "vue";
import moment from "moment/moment.js";
import {trans} from "laravel-vue-i18n";
import {DateTime} from "luxon";

const props = defineProps({
    dateTimeRestrictions: Object,
    selectedDate: Date,
    events: Array,
    timeSlots: Array,
    theme: Object,
    compute: [String, Number],
    selectedService: Object
});

watch(() => props.selectedDate, () => {
    selectedTimeSlot.value = null;
});

const selectedTimeSlot = defineModel();

const takenObj = ref({
    message: trans('widget.all_slots_taken'),
    severity: 'error'
});

const allTimeSlotsTaken = computed(() => {
    let areTheyTaken = true;
    computedTimeSlots.value?.forEach((value) => {
        if (!value.disabled)
            areTheyTaken = false;
    });
    // Co když je vybraný datum součástí zakázaných datumů
    props.dateTimeRestrictions.disabled.forEach((value) => {
        let startMoment = moment(value.start);
        let endMoment = moment(value.repeat.until);
        let selectedMoment = moment(props.selectedDate);
        if ((value.start === props.selectedDate) || selectedMoment.isBetween(startMoment, endMoment) || (value.repeat.until === props.selectedDate)) {
            // Dny v týdnu
            if (value.repeat.weekdays && value.repeat.weekdays.includes(props.selectedDate.getDay() + 1)) {
                areTheyTaken = true;
                takenObj.value.severity = 'warn';
                takenObj.value.message = trans('widget.day_unavailable');
            }
            // Dny v měsíci
            if (value.repeat.days && value.repeat.days.includes(props.selectedDate.getDate())) {
                takenObj.value.severity = 'warn';
                areTheyTaken = true;
                takenObj.value.message = trans('widget.day_unavailable');
            }
            // Čísla týdnů
            if (value.repeat.weeks && value.repeat.weeks.includes(props.selectedDate.getWeek())) {
                takenObj.value.severity = 'warn';
                areTheyTaken = true;
                takenObj.value.message = trans('widget.week_unavailable');
            }
            // Měsíce
            if (value.repeat.months && value.repeat.months.includes(props.selectedDate.getMonth() + 1)) {
                takenObj.value.severity = 'warn';
                areTheyTaken = true;
                takenObj.value.message = trans('widget.month_unavailable');
            }
            // Roky
            if (value.repeat.years && value.repeat.years.includes(props.selectedDate.getFullYear())) {
                takenObj.value.severity = 'warn';
                areTheyTaken = true;
                takenObj.value.message = trans('widget.year_unavailable');
            }
        }
    });

    return areTheyTaken;
});

const computedTimeSlots = computed(() => {
    const formatter = new Intl.DateTimeFormat("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    })

    let dateValue = props.selectedDate;
    let selectedDateClean = formatter.format(dateValue);

    let returningTimeslots = Object.values(props.timeSlots ?? {});
    let eventsFormatted = props.events?.map((value) => {
        let startDateTime = new Date(value.startDate ?? '1999-01-01T00:00:00');
        let endDateTime = new Date(value.endDate ?? '1999-01-01T00:00:00');
        return {
            start: {
                date: formatter.format(startDateTime),
                time: ("0" + startDateTime.getHours()).slice(-2) + ':' + ("0" + startDateTime.getMinutes()).slice(-2)
            },
            end: {
                date: formatter.format(endDateTime),
                time: ("0" + endDateTime.getHours()).slice(-2) + ':' + ("0" + endDateTime.getMinutes()).slice(-2)
            },
            title: value.title ?? '',
            wholeDay: value.wholeDay ?? false
        }
    });
    if (returningTimeslots[0]?.blocking === 0) {
        let start = moment('2024-01-01T00:00:00');
        let end = moment('2024-01-01T23:30:00');
        let addMinutes = 30;
        let pause = 0;
        let serviceLengthOverride = returningTimeslots[0]?.service_length_override;

        if (returningTimeslots[0]?.blocking === 0) {
            start = moment('2024-01-01T' + returningTimeslots[0].time_start);
            end = moment('2024-01-01T' + returningTimeslots[0].time_end);

            addMinutes = returningTimeslots[0]?.interval ?? 30;
            pause = returningTimeslots[0]?.pause ?? 0;
            returningTimeslots = [];
        }

        let addingSlots = true;
        let currentStart = start.clone(); // Používáme .clone() pro zachování původní hodnoty
        let currentEnd = currentStart.clone().add(addMinutes, 'minutes');
        returningTimeslots.push({
            time_start: currentStart.format('HH:mm:ss'),
            time_end: currentEnd.format('HH:mm:ss'),
            id: currentStart.format('HH:mm:ss') + '/' + currentEnd.format('HH:mm:ss'),
            service_length_override: serviceLengthOverride,
        });
        let safeCounter = 0;
        while (addingSlots) {
            currentStart = currentEnd.clone().add(pause, 'minutes'); // Znovu klonujeme hodnotu, aby nedošlo k přepisu

            currentEnd = currentStart.clone().add(addMinutes, 'minutes');

            if (currentEnd.isSameOrAfter(end)) {
                addingSlots = false;
                break;
            }

            returningTimeslots.push({
                time_start: currentStart.format('HH:mm:ss'),
                time_end: currentEnd.format('HH:mm:ss'),
                id: currentStart.format('HH:mm:ss') + '/' + currentEnd.format('HH:mm:ss'),
                service_length_override: serviceLengthOverride,
            });


            if (safeCounter > 500) {
                console.log('died');
                addingSlots = false;
            }
            safeCounter++;
        }
    }

    let selectedDateEvents = eventsFormatted.filter((value) => {
        let startMoment = moment(value.start.date);
        let endMoment = moment(value.end.date);
        let selectedMoment = moment(props.selectedDate);
        return (value.start.date === selectedDateClean) || selectedMoment.isBetween(startMoment, endMoment) || (value.end.date === selectedDateClean)
    });

    const allSlotEnds = returningTimeslots.map((value) => DateTime.fromSQL((value.time_end)).toUnixInteger());

    returningTimeslots = returningTimeslots?.map((value) => {
        let disableIt = false;
        const realTimeEnd = value.real_time_end;
        let thisTimeSlot = [
            moment('2024-01-01T' + value.time_start),
            getSlotEnd(value)
        ];

        let checkServiceLengthOverride = false;

        value.last = false;

        if (!value.blocking) {
            const realSlotEnd = DateTime.fromSQL((realTimeEnd)).toUnixInteger();
            if (Math.max(...allSlotEnds) === realSlotEnd) {
                checkServiceLengthOverride = !value.service_length_override;
            }
        } else {
            checkServiceLengthOverride = !value.service_length_override;
        }

        if (checkServiceLengthOverride && props.selectedService) {
            const realSlotLength = moment.duration(moment('2024-01-01T' + realTimeEnd).diff(moment('2024-01-01T' + value.time_start)));
            const realMinutes = (realSlotLength.hours() * 60) + realSlotLength.minutes() + (realSlotLength.seconds() / 60);
            const realServiceLength = props.selectedService?.length_in_minutes ?? 0;
            value.last = !(realMinutes >= realServiceLength);
        }

        value.disabled = false;

        value.time_end = getSlotEnd(value).format('HH:mm:ss');

        value.id = `${value.time_start}/${value.time_end}`;

        selectedDateEvents.forEach((value) => {
            if (value.wholeDay) {
                takenObj.value.message = trans('widget.day_disabled');
                takenObj.value.severity = 'error';
                disableIt = true;
                return;
            }

            let currentTimeSlot = [
                moment('2024-01-01T' + (value.start.time) + ':00'),
                moment('2024-01-01T' + (value.end.time) + ':00')
            ];

            // TODO: tady vzniká ta chyba, kde dáš seemingly okay timeslot, ale stejně ti to hodí chybu z backendu. Víš proč?
            // no... jednoduše, jsou tam několikadenní timesloty, ale tyhle timesloty mají čas začátku a konce, booyah!

            if (rangeOverlap(thisTimeSlot, currentTimeSlot)) {
                disableIt = true;
                takenObj.value.message = trans('widget.slots_unavailable');
                takenObj.value.severity = 'error';
            }
        });

        if (disableIt)
            value.disabled = true;

        return value;
    })


    return returningTimeslots?.sort((a, b) => {
        const first = Number(a.time_start.split(':').join(''));
        const second = Number(b.time_start.split(':').join(''));
        return first - second;
    });
});

const rangeOverlap = (range1, range2) => {
    const [start1, end1] = range1;
    const [start2, end2] = range2;

    return (start1.isBefore(end2) && end1.isAfter(start2));
}

const getSlotEnd = (slot) => {
    const start = moment('2024-01-01T' + slot.time_start);
    if (props.compute === 'slot') {
        return moment('2024-01-01T' + slot.time_end);
    }
    return start.clone().add(Number(props.compute), 'minutes');
};

const humanTimeDiff = (start, end, lang = 'cs') => {
    lang = lang.split('_')[0];

    // Rozpoznání formátu podle délky stringu
    const timeFormat = start.length === 5 ? "HH:mm" : "HH";

    const startTime = moment(start, timeFormat);
    const endTime = moment(end, timeFormat);

    const duration = moment.duration(endTime.diff(startTime));

    const hours = duration.hours();
    const minutes = duration.minutes();

    // Překlady podle jazyka
    const translations = {
        'cs': {hour: 'hodina', hours: 'hodiny', minutes: 'minut', minute: 'minuta'},
        'sk': {hour: 'hodina', hours: 'hodiny', minutes: 'minút', minute: 'minúta'},
        'en': {hour: 'hour', hours: 'hours', minutes: 'minutes', minute: 'minute'},
        'ko': {hour: '시간', hours: '시간', minutes: '분', minute: '분'},
    };

    // Vybereme překlad pro zadaný jazyk, výchozí je čeština
    const t = translations[lang] || translations['cs'];

    let result = '';
    if (hours > 0) {
        result += `${hours} ${hours === 1 ? t.hour : t.hours}`;
    }
    if (minutes > 0) {
        if (hours > 0) result += ' a ';
        result += `${minutes} ${minutes === 1 ? t.minute : t.minutes}`;
    }

    return result || `0 ${t.minutes}`;
};
const getSlotClass = (timeSlot) => {
    if (timeSlot.last) {
        return 'cursor-no-drop bg-neutral-100 dark:bg-neutral-900 text-neutral-500';
    }
    if (timeSlot.disabled) {
        return 'cursor-no-drop bg-neutral-200 dark:bg-neutral-900 text-neutral-500';
    }
    if (selectedTimeSlot.value === timeSlot.id) {
        return `ring-2 ring-${props.theme.color}-500 bg-${props.theme.color}-100 dark:!bg-${props.theme.color}-800`;
    }
    return `cursor-pointer hover:bg-${props.theme.color}-50 hover:dark:bg-${props.theme.color}-600 dark:bg-transparent bg-white`;

}
</script>

<template>
    <div>
        <template v-if="!allTimeSlotsTaken && selectedDate">
            <div class="grid grid-cols-2 md:grid-cols-4 gap-3 rounded-lg p-3">
                <div
                    class="p-3 border border-neutral-300 flex flex-col items-center justify-center transition-all duration-200 ease-in-out hover:shadow-md"
                    :class="[{
                        'rounded-lg': theme.roundness === 1,
                        'rounded-xl': theme.roundness === 2,
                        'rounded-2xl': theme.roundness === 3,
                        'border-neutral-700 dark:border-neutral-700 bg-transparent hover:bg-transparent': theme.darkMode
                    }, getSlotClass(timeSlot)]"
                    @click="!timeSlot.disabled && !timeSlot.last && (selectedTimeSlot = timeSlot.id)"
                    v-for="(timeSlot, t) in computedTimeSlots" :key="t"
                >
                    <div class="flex items-center gap-1">
                        <i class="pi pi-arrow-down opacity-50" style="font-size: .7rem"></i>
                        <div class="text-center">
                            <p class="text-md font-medium">{{ timeSlot.time_start.slice(0, -3) }}</p>
                            <p class="text-md font-medium">{{ getSlotEnd(timeSlot).format('HH:mm') }}</p>
                        </div>
                        <i class="pi pi-arrow-down opacity-50" style="font-size: .7rem"></i>
                    </div>
                    <p v-if="!timeSlot.disabled && !timeSlot.last"
                       class="text-sm text-neutral-600 dark:text-neutral-200 text-center">{{
                            humanTimeDiff(
                                timeSlot.time_start.slice(0, -3),
                                getSlotEnd(timeSlot).format('HH:mm'),
                                $page.props.locale
                            )
                        }}</p>
                    <p v-else-if="timeSlot.last" class="text-xs text-neutral-600 text-center">{{
                            $t('widget.too_long')
                        }}</p>
                    <p v-else class="text-sm text-neutral-600">{{ $t('widget.unavailable') }}</p>
                </div>
            </div>
        </template>
        <InlineMessage v-else :severity="takenObj.severity" class="m-4 mr-0">{{ takenObj.message }}</InlineMessage>
    </div>
</template>

<style scoped lang="scss">

</style>
